/**
 * Shared client-side types mirroring server DTOs. Keep these in sync with their C#
 * counterparts in {ProjectName}.Models.Dtos — the API uses camelCase JSON.
 */

// --- Paging --------------------------------------------------------------------------

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface QueryOptions {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDesc?: boolean;
  filter?: string;
}

// --- Base entity fields any persisted entity DTO carries ----------------------------

export interface AuditFields {
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

// --- Core types ---------------------------------------------------------------------

export type HealthDto = {
  status: string;
  version: string;
  utcNow: string;
  environment: string;
};

export type CurrentUserDto = {
  email: string | null;
  displayName: string | null;
  roles: string[];
  isAuthenticated: boolean;
};

export type AdminUserDto = {
  id: string;
  email: string | null;
  userName: string | null;
  emailConfirmed: boolean;
  disabled: boolean;
  lockoutEnd: string | null;
  roles: string[];
  source: 'Identity' | 'LocalAssignment' | string;
};

export type AdminRoleDto = { name: string; userCount: number };
export type AdminCapabilities = { supportsLocalAccounts: boolean };
export type CreateUserRequest = { email: string; password: string; roles: string[] };

// --- Work board (mirrors Thunderdome.Models/Dtos/WorkBoardDtos.cs) -------------------

export type CardSection = 'Backlog' | 'Planning' | 'Ready' | 'Working';

/** Display order, top to bottom: board columns, print rows and client-side sorting all use this. */
export const CARD_SECTIONS: readonly CardSection[] = ['Working', 'Ready', 'Planning', 'Backlog'];

export interface CardDto {
  id: number;
  workStreamId: number;
  section: CardSection;
  sortOrder: number;
  title: string;
  description: string | null;
  /** One-time saving in dollars. */
  roiOneTime: number | null;
  /** Recurring saving per year in dollars. */
  roiAnnual: number | null;
  /** Optional calculator lines; stored as sent, the server does not recompute totals. */
  roiBreakdown: RoiLine[] | null;
  roiExplanation: string | null;
  workersNeeded: number;
  estimateWeeks: number;
  productOwnerName: string | null;
  dependencyCardId: number | null;
  dependencyCardTitle: string | null;
  /** Cost to build: tokens, in dollars. */
  tokensCost: number | null;
  /** Cost to build: anything else, in dollars. */
  additionalCost: number | null;
  /** Server-owned "yyyy-MM-dd": set when the card enters Working (today + estimate), cleared when it leaves. */
  expectedDate: string | null;
  /** Calendar month 1-12 the card is marked for, shown as a coloured pill on the tile. */
  targetMonth: number | null;
}

export type RoiLineKind = 'Labor' | 'AvoidedHire' | 'Contract' | 'Other';
export type RoiPeriod = 'OneTime' | 'PerMonth' | 'PerQuarter' | 'PerYear';

export interface RoiLine {
  kind: RoiLineKind;
  label: string | null;
  amount: number;
  period: RoiPeriod;
  /** Labor helper inputs; when both are set, amount = hours x rate x 52 per year. */
  hoursPerWeek: number | null;
  hourlyRate: number | null;
}

export const ROI_LINE_KINDS: readonly RoiLineKind[] = ['Labor', 'AvoidedHire', 'Contract', 'Other'];
export const ROI_PERIODS: readonly RoiPeriod[] = ['OneTime', 'PerMonth', 'PerQuarter', 'PerYear'];

export const ROI_LINE_KIND_LABELS: Record<RoiLineKind, string> = {
  Labor: 'Labor savings',
  AvoidedHire: 'Avoided hire',
  Contract: 'Contract savings',
  Other: 'Other',
};

export const ROI_PERIOD_LABELS: Record<RoiPeriod, string> = {
  OneTime: 'One-time',
  PerMonth: 'Per month',
  PerQuarter: 'Per quarter',
  PerYear: 'Per year',
};

export interface StreamDto {
  id: number;
  name: string;
  workerCount: number;
  /** Server-derived: sum of workersNeeded of this stream's Working cards. */
  usedWorkers: number;
  /** Ordered by section, then sortOrder. */
  cards: CardDto[];
}

export interface BoardDto {
  /** Ordered by id. */
  streams: StreamDto[];
}

export interface CardUpsertDto {
  /** Used on create only; ignored by the server on update. */
  workStreamId: number;
  title: string;
  description: string | null;
  roiOneTime: number | null;
  roiAnnual: number | null;
  /** Empty list is sent as null. */
  roiBreakdown: RoiLine[] | null;
  roiExplanation: string | null;
  workersNeeded: number;
  estimateWeeks: number;
  productOwnerName: string | null;
  dependencyCardId: number | null;
  tokensCost: number | null;
  additionalCost: number | null;
  targetMonth: number | null;
}

export interface MoveCardDto {
  workStreamId: number;
  section: CardSection;
  /** 0-based target index in the destination section, computed after removing the card. */
  sortOrder: number;
}

export interface WorkStreamUpsertDto {
  name: string;
  workerCount: number;
}

// --- Board commits (snapshots of the whole board) -----------------------------------

export interface CreateBoardCommitDto {
  /** Optional, max 200. */
  note: string | null;
}

export interface BoardCommitSummary {
  id: number;
  /** ISO UTC. */
  createdAt: string;
  createdBy: string | null;
  note: string | null;
  streamCount: number;
  cardCount: number;
}

/** A snapshot: the summary plus the board exactly as `GET /api/work/board` returned it then. */
export interface BoardCommit extends BoardCommitSummary {
  board: BoardDto;
}

export type AuthProvider ='Identity' | 'Okta' | 'Entra' | string;

export interface ClientConfigDto {
  appInsightsConnectionString: string | null;
  environment: string;
  authProvider: AuthProvider;
  /** Present only in Okta/Entra mode — the OIDC issuer/authority URL. */
  oidcAuthority: string | null;
  /** Present only in Okta/Entra mode — the SPA public client id. */
  oidcClientId: string | null;
  /** Present only in Okta/Entra mode — space-delimited scope string. */
  oidcScope: string | null;
}
